"use client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import {
  Award,
  Briefcase,
  Building2,
  Calendar,
  Download,
  Eye,
  Mail,
  MapPin,
  Phone,
  User,
  X,
  Trash2,
  BadgeCheck,
} from "lucide-react";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import {
  fetchRegistrations,
  deleteEventAqua,
} from "../../../services/eventsServices";
import axiosInstance from "../../../lib/axios";
import DeleteConfirmationModal from "../../../components/admin/members/DeleteMemberModal";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { getCookie } from "../../../utils";

const SapAquaEvents = () => {
  const topRef = useRef(null);
  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    hasPrev: false,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["aqua-2026", currentPage, entriesOption.value],
    queryFn: () =>
      fetchRegistrations({ page: currentPage, limit: entriesOption.value }),
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteEventAqua(id),
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      refetch();
    },
    onError: (error) => {
      alert(error?.message || "Failed to delete event");
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(
        "/event-register-aqua-2026/export/excel",
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `registrations_aqua2026_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    },
    onError: (error) => {
      console.error("Failed to download Excel file:", error);
      alert(
        error?.response?.data?.message ||
          "Failed to download Excel file. Please try again."
      );
    },
  });

  useEffect(() => {
    if (data?.data?.data) {
      setEvents(data.data.data);
      setPagination({
        totalPages: data.data.pagination?.totalPages || 1,
        totalCount: data.data.total || 0,
        hasMore: currentPage < (data.data.pagination?.totalPages || 1),
        hasPrev: currentPage > 1,
      });
    }
  }, [data, currentPage]);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setCurrentPage(pageNumber);
      scrollToTop();
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentPage(1);
    scrollToTop();
  };

  const closeModal = () => setSelectedEvent(null);

  const handleExport = () => {
    exportMutation.mutate();
  };

  const openDeleteModal = (event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (eventToDelete?._id) {
      deleteMutation.mutate(eventToDelete._id);
    }
  };

  const userId = getCookie("userId");
  const role = getCookie("role");

  // Fetch permissions for maintainer
  const { data: permissionsData, isLoading: permissionsLoading } =
    useUserModulePermissions("Events", userId, {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    });

  // Check if user has delete permission
  const hasDeletePermission = () => {
    // Admin has all permissions
    if (role === "admin") {
      return true;
    }

    // Check maintainer permissions
    if (role === "maintainer" && permissionsData?.data?.permissions) {
      const eventsPermission = permissionsData.data.permissions.find(
        (perm) => perm.module === "Events"
      );
      return eventsPermission?.delete === true;
    }

    return false;
  };

  const canDelete = hasDeletePermission();

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <Sidebar />

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] p-3 overflow-hidden flex flex-col">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div ref={topRef} />

            <Header
              title="Aqua Event 2026"
              desc="Complete list of participants registered for the SAP Aqua Event 2026."
            />

            <div className="mb-4 flex justify-between items-center">
              <DropdownSelect
                selected={entriesOption}
                onChange={handleEntriesChange}
              />

              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  {pagination.totalCount > 0 &&
                    `Showing page ${currentPage} of ${pagination.totalPages} (${pagination.totalCount} total events)`}
                </div>

                <button
                  onClick={handleExport}
                  disabled={exportMutation.isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#4F22D8] text-white rounded-lg hover:bg-[#6B3FE8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  aria-label="Export registrations to Excel"
                >
                  {exportMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Export Excel
                    </>
                  )}
                </button>
              </div>
            </div>

            {isLoading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-2">Loading events...</span>
              </div>
            )}

            {isError && (
              <div className="text-red-500 text-center mt-4">
                <p>Error loading events: {error?.message}</p>
                <button
                  onClick={() => refetch()}
                  className="flex items-center gap-2 px-4 py-2 mt-2 bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200 mx-auto"
                >
                  Retry
                </button>
              </div>
            )}

            <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm bg-white mt-1 flex-1">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-[#4F22D8] text-white">
                  <tr>
                    <th className="w-[18%] px-4 py-3 text-left text-sm font-semibold">
                      Name
                    </th>
                    <th className="w-[16%] px-4 py-3 text-left text-sm font-semibold">
                      Designation
                    </th>
                    <th className="w-[22%] px-4 py-3 text-left text-sm font-semibold">
                      Company
                    </th>
                    <th className="w-[13%] px-4 py-3 text-left text-sm font-semibold">
                      Mobile
                    </th>

                    <th className="w-[15%] px-4 py-3 text-left text-sm font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {events.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No events found.
                      </td>
                    </tr>
                  ) : (
                    events.map((registration) => (
                      <tr
                        key={registration._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="line-clamp-2 break-words">
                            {registration.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="line-clamp-2 break-words">
                            {registration.designation}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="line-clamp-2 break-words leading-relaxed">
                            {registration.company}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="break-words">
                            {registration.mobile}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-3">
                            <button
                              onClick={() => setSelectedEvent(registration)}
                              className="flex items-center gap-1.5 text-[#4F22D8] hover:underline font-semibold text-sm"
                              aria-label={`View details of ${registration.name}`}
                            >
                              <Eye size={16} />
                              View
                            </button>
                            {canDelete && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(registration);
                                }}
                                className="flex items-center gap-1.5 text-red hover:underline font-semibold text-sm"
                                aria-label={`Delete ${registration.name}`}
                              >
                                <Trash2 size={16} />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  totalPages={pagination.totalPages}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  showPages={5}
                />
              </div>
            )}

            {/* Modal for viewing full event details */}
            {selectedEvent && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="bg-white rounded-2xl max-w-2xl w-full relative shadow-2xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r flex items-center from-[#4F22D8] to-[#6B3FE8] px-8 h-24 mb-0 pb-0 relative">
                    <button
                      onClick={closeModal}
                      className="absolute top-6 right-6 text-white hover:bg-white/20 rounded-full h-10 w-10 transition-all"
                      aria-label="Close modal"
                    >
                      <X size={20} />
                    </button>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-white">
                        Registration Details
                      </span>
                      <p className="text-purple-100 leading-0 text-sm mt-1">
                        Complete attendee information
                      </p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 overflow-y-auto max-h-[calc(90vh-180px)] space-y-2">
                    {/* Name */}
                    <div className="flex items-start gap-2 p-4 bg-[#F3EFFF] rounded-xl">
                      <div className="mt-0.5 text-[#4F22D8]">
                        <User size={20} />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Name
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedEvent.name}
                        </p>
                      </div>
                    </div>

                    {/* Designation & Company */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <Briefcase size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Designation
                          </p>
                          <p className="text-gray-900 font-semibold">
                            {selectedEvent.designation}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <Building2 size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Company
                          </p>
                          <p className="text-gray-900 font-semibold">
                            {selectedEvent.company}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <Phone size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Mobile
                          </p>
                          <p className="text-gray-900 font-semibold">
                            {selectedEvent.mobile}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <Mail size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Email
                          </p>
                          <a
                            href={`mailto:${selectedEvent.email}`}
                            className="text-[#4F22D8] hover:underline font-semibold break-all"
                          >
                            {selectedEvent.email}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Location & Membership */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <MapPin size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Location
                          </p>
                          <p className="text-gray-900 font-semibold capitalize">
                            {selectedEvent.location}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-[#F3EFFF] rounded-xl">
                        <div className="mt-0.5 text-[#4F22D8]">
                          <Award size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                            Membership Type
                          </p>
                          <p className="text-gray-900 font-semibold capitalize">
                            {selectedEvent.membershipType}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Registration Date */}
                    <div className="flex items-start gap-2 p-4 bg-[#F3EFFF] rounded-xl">
                      <div className="mt-0.5 text-[#4F22D8]">
                        <Calendar size={20} />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Registered At
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {new Date(selectedEvent.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              onRequestClose={() => setIsDeleteModalOpen(false)}
              onDelete={confirmDelete}
              itemName={eventToDelete?.name || ""}
              itemType="event"
            />

            <div className="h-5" />
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default SapAquaEvents;
